import { capitalize } from '@mui/material';
import { debounce } from '@mui/material/utils';
import { Tag } from '@strength/domain';
import { createColumnHelper } from '@tanstack/react-table';
import { getRouteApi } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 } from 'uuid';
import { trpc } from '../../../../trpc/react';
import { Gallery } from '../../../components/gallery/gallery';
import { SelectableOption } from '../../../components/input/multi-select';
import { useFilteredGalleryPagination } from '../../../hooks/useFilteredGalleryPagination';
import { useToast } from '../../../hooks/useToast';
import { WorkoutTemplateCard } from './components/WorkoutTemplateCard';
import { WorkoutTemplateDrawer } from './components/WorkoutTemplateDrawer';
import { LibraryWorkoutTemplate } from './types';
import { WorkoutTemplateData } from './workout-template-utils';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../../auth';
import { getTrpcProxyClient } from '../../../../trpc';

const columnHelper = createColumnHelper<LibraryWorkoutTemplate>();

const routeApi = getRouteApi(
  '/orgAuth/orgWithNav/$teamId/library/workout-templates',
);

export function TemplatesPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { teamId } = routeApi.useParams();
  const _selectedLibraryTeamId = teamId || undefined;
  const [searchInput, setSearchInput] = useState<string>('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
  const [selectedExerciseOptions, setSelectedExerciseOptions] = useState<
    SelectableOption[]
  >([]);
  const [selectedWorkoutCount, setSelectedWorkoutCount] = useState<
    number | undefined
  >();

  const {
    refetch,
    data,
    pageSize,
    setPageSize,
    page,
    setPage,
    viewMode,
    setViewMode,
    isLoading,
  } = useFilteredGalleryPagination({
    queryFn: trpc.workoutTemplateLibrary.templateSearch.useQuery,
    queryInput: {
      searchTerm: debouncedSearchTerm,
      tagIds: selectedTags.map((tag) => tag.id),
      exerciseIds: selectedExerciseOptions.map((option) => option.id),
      workoutCount: selectedWorkoutCount,
    },
  });

  // Fetch exercises used in templates for the filter options (deduped — these
  // ids are sent in the exerciseOptionsByIds GET query string, which overflows
  // on large libraries)
  const referenceExerciseIds = [
    ...new Set(data?.records.flatMap((r) => r.exerciseIds) ?? []),
  ];

  // This allows us to treat the mutate call as a GET request so caching
  // and the `enabled` flag works correctly
  const auth = useAuth();
  const trpcClient = getTrpcProxyClient(() => auth.setAuth(null));
  const { data: exerciseOptions } = useQuery({
    queryKey: ['exerciseOptionsByIds', referenceExerciseIds],
    queryFn: () =>
      trpcClient.exerciseLibrary.exerciseOptionsByIds.mutate({
        libraryExerciseIds: referenceExerciseIds,
      }),
    enabled: referenceExerciseIds.length > 0,
  });

  // Fetch tag options for workout_template type
  const tagsQuery = trpc.tag.tags.useQuery({
    types: ['workout_template'],
  });
  const tagOptions = useMemo(() => tagsQuery.data || [], [tagsQuery.data]);

  // Deduplicate exercise options
  const uniqueExerciseOptions = useMemo(
    () =>
      exerciseOptions?.filter(
        (e, index, self) =>
          index === self.findIndex((exercise) => exercise.id === e.id),
      ) ?? [],
    [exerciseOptions],
  );

  // Mutations
  const editWorkoutTemplate =
    trpc.workoutTemplateLibrary.editWorkoutTemplate.useMutation({
      onSuccess: () => {
        refetch();
      },
    });

  const deleteWorkoutTemplate =
    trpc.workoutTemplateLibrary.deleteWorkoutTemplate.useMutation({
      onSuccess: () => {
        refetch();
      },
    });

  const onSaveWorkoutTemplate = useCallback(
    (workoutTemplate: WorkoutTemplateData) => {
      const { name, tags, id } = workoutTemplate;
      const tagSelections = tags.map((tag, i) => ({
        id: v4(),
        sortOrder: i,
        tag,
      }));
      editWorkoutTemplate.mutate(
        { id, name, tags: tagSelections },
        {
          onSuccess: () =>
            toast({
              title: t('wasUpdated', { name }),
              severity: 'success',
            }),
          onError: () =>
            toast({
              title: t('somethingWentWrong'),
              severity: 'error',
            }),
        },
      );
    },
    [editWorkoutTemplate, t, toast],
  );

  const onDeleteWorkoutTemplate = useCallback(
    (templateId: string, name: string) => {
      deleteWorkoutTemplate.mutate(
        { id: templateId },
        {
          onSuccess: () =>
            toast({
              title: t('wasDeleted', { name }),
              severity: 'success',
            }),
          onError: () =>
            toast({
              title: t('somethingWentWrong'),
              severity: 'error',
            }),
        },
      );
    },
    [deleteWorkoutTemplate, t, toast],
  );

  // Distinct day counts across the whole filtered set, from the server facet —
  // not derived from the current page, so counts on unvisited pages stay
  // selectable even when a filter narrows the visible records
  const workoutCountOptions = useMemo(() => {
    // leading "any" option clears the filter without touching the others
    const clearOption = {
      id: 'any',
      label: t('all'),
      value: undefined as number | undefined,
    };
    return [
      clearOption,
      ...(data?.workoutCounts ?? [])
        .sort((a, b) => a - b)
        .map((count) => ({
          id: String(count),
          label: t('workoutCount', { count }),
          value: count,
        })),
    ];
  }, [data?.workoutCounts, t]);

  // Table columns
  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: () => t('name'),
        cell: ({ row }) => capitalize(row.original.name),
      }),
      columnHelper.accessor('tags', {
        header: () => t('tags'),
        cell: ({ row }) =>
          row.original.tags.map((tag) => capitalize(tag.tag.name)).join(', '),
      }),
      ...[
        columnHelper.accessor((row) => row.template.length, {
          id: 'workoutCount',
          header: () => t('numberOfWorkouts'),
          cell: ({ getValue }) => getValue(),
        }),
      ],
    ],
    [t],
  );

  // Debounce the search term
  const debouncedSetSearchTerm = useMemo(
    () => debounce((value: string) => setDebouncedSearchTerm(value), 300),
    [],
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchInput(value);
      debouncedSetSearchTerm(value);
    },
    [debouncedSetSearchTerm],
  );

  const handleResetFilters = useCallback(() => {
    setSearchInput('');
    setDebouncedSearchTerm('');
    debouncedSetSearchTerm.clear();
    setSelectedTags([]);
    setSelectedExerciseOptions([]);
    setSelectedWorkoutCount(undefined);
  }, [debouncedSetSearchTerm]);

  // Gallery filters
  const filters = useMemo(
    () => [
      {
        id: 'tags',
        type: 'select' as const,
        title: t('tags'),
        multiselect: true,
        showSearch: true,
        showSelectAll: true,
        options: tagOptions.map((tag) => ({
          id: tag.id,
          label: tag.name,
          value: tag,
        })),
      },
      {
        id: 'exercises',
        type: 'select' as const,
        title: t('includesExercise'),
        multiselect: true,
        showSearch: true,
        showSelectAll: true,
        options: uniqueExerciseOptions.map((exercise) => ({
          id: exercise.id,
          label: exercise.name,
          value: exercise,
        })),
      },
      ...[
        {
          id: 'workoutCount',
          type: 'select' as const,
          title: t('numberOfWorkouts'),
          multiselect: false,
          options: workoutCountOptions,
        },
      ],
    ],
    [t, tagOptions, uniqueExerciseOptions, workoutCountOptions],
  );

  const handleFilterChange = useCallback(
    (filterValues: Record<string, (Tag | SelectableOption | number)[]>) => {
      const tagsFilter = filterValues['tags'];
      if (tagsFilter !== undefined) {
        setSelectedTags(tagsFilter as Tag[]);
      }

      const exercisesFilter = filterValues['exercises'];
      if (exercisesFilter !== undefined) {
        setSelectedExerciseOptions(exercisesFilter as SelectableOption[]);
      }

      // single-select exact match — Gallery always dispatches arrays
      const workoutCountFilter = filterValues['workoutCount'];
      if (workoutCountFilter !== undefined) {
        setSelectedWorkoutCount(
          (workoutCountFilter[0] as number | undefined) ?? undefined,
        );
      }
    },
    [],
  );

  return (
    <Gallery<LibraryWorkoutTemplate>
      title={t('templates')}
      createButtonText={t('createWorkoutTemplate')}
      data={data}
      columns={columns}
      filters={filters}
      viewMode={viewMode}
      isLoading={isLoading}
      setViewMode={setViewMode}
      page={page}
      pageSize={pageSize}
      setPage={setPage}
      setPageSize={setPageSize}
      searchValue={searchInput}
      onSearchChange={handleSearchChange}
      onFilterChange={handleFilterChange}
      onResetFilters={handleResetFilters}
      renderCard={(item, viewMode, setSelected) => (
        <WorkoutTemplateCard
          key={item.id}
          item={item}
          viewMode={viewMode}
          onClick={setSelected}
        />
      )}
      renderEditSheet={(selected, setSelected) => {
        // Storage keeps exactly one entry per day (single-day templates are
        // collapsed to one layer at save), so entry count > 1 means multi-day
        const isMultiDay = selected.template.length > 1;
        const template: WorkoutTemplateData = {
          id: selected.id,
          name: selected.name,
          tags: selected.tags.map((tag) => tag.tag),
          template: selected.template,
          isMultiDay,
        };
        return (
          <WorkoutTemplateDrawer
            // Remount on template change so drawer-local state (localTemplate,
            // day list uids) never carries over between templates
            key={selected.id}
            mode="edit"
            workoutTemplate={template}
            tagOptions={tagOptions}
            onClose={() => setSelected(null)}
            onSave={onSaveWorkoutTemplate}
            onDelete={onDeleteWorkoutTemplate}
          />
        );
      }}
    />
  );
}
